import { formatVariantLabel } from 'lib/format-variant';

export type VariantRulesKey = 'classic' | 'ak47' | 'muflis';

export interface HandRankingRow {
  rank: number;
  name: string;
  example: string;
}

export interface VariantRulesContent {
  key: VariantRulesKey;
  title: string;
  description: string;
  objective: string;
  handRankings: HandRankingRow[];
  handRankingNote?: string;
  extraSections: Array<{ title: string; body: string | string[] }>;
  examples?: Array<{ label: string; original: string; result: string; note?: string }>;
}

const CLASSIC_HANDS: HandRankingRow[] = [
  { rank: 1, name: 'Trail', example: 'A♥ A♦ A♣' },
  { rank: 2, name: 'Pure Sequence', example: 'A♥ K♥ Q♥' },
  { rank: 3, name: 'Sequence', example: '7♠ 8♦ 9♣' },
  { rank: 4, name: 'Color', example: '2♦ 9♦ K♦' },
  { rank: 5, name: 'Pair', example: 'J♥ J♣ 4♠' },
  { rank: 6, name: 'High Card', example: 'A♠ J♥ 7♦' },
];

const MUFLIS_HANDS: HandRankingRow[] = [
  { rank: 1, name: 'High Card', example: '2♣ 5♦ 8♥' },
  { rank: 2, name: 'Pair', example: '5♠ 5♥ K♦' },
  { rank: 3, name: 'Color', example: '4♦ 7♦ J♦' },
  { rank: 4, name: 'Sequence', example: 'A♣ 2♦ 3♥' },
  { rank: 5, name: 'Pure Sequence', example: '2♥ 3♥ 4♥' },
  { rank: 6, name: 'Trail', example: 'A♠ A♥ A♦' },
];

const SHARED_GAMEPLAY = [
  'Boot — mandatory ante collected from every player before cards are dealt.',
  'Blind — bet without seeing cards (half the seen rate).',
  'See — view your cards; subsequent bets cost double.',
  'Chaal — match the current stake.',
  'Raise — increase the stake within table limits.',
  'Pack (Fold) — forfeit the hand and lose bets already placed.',
  'Show — allowed with exactly 2 active players; triggers showdown.',
];

export const VARIANT_RULES: Record<VariantRulesKey, VariantRulesContent> = {
  classic: {
    key: 'classic',
    title: 'Classic Rules',
    description: 'Standard Teen Patti — the strongest traditional 3-card hand wins the pot.',
    objective: 'Build the highest-ranked hand. No jokers — every card keeps its face value.',
    handRankings: CLASSIC_HANDS,
    handRankingNote: 'Strongest hand wins. Compare category first, then tie-breakers within the same category.',
    extraSections: [
      { title: 'How to Play', body: SHARED_GAMEPLAY },
      {
        title: 'Turn Timer',
        body: 'Each player has a limited time to act. If time expires, you are automatically packed (folded).',
      },
    ],
  },
  ak47: {
    key: 'ak47',
    title: 'AK47 Rules',
    description: 'High-action Teen Patti where Aces, Kings, 4s and 7s act as jokers.',
    objective: 'Build the strongest possible hand — jokers auto-complete to your best ranking.',
    handRankings: CLASSIC_HANDS,
    handRankingNote:
      'Hand categories match Classic (Trail highest). A, K, 4 and 7 are wild — the system evaluates all joker combinations and assigns the highest possible rank. You cannot choose joker values manually.',
    extraSections: [
      { title: 'How to Play', body: SHARED_GAMEPLAY },
      {
        title: 'Joker Cards',
        body: [
          'Aces, Kings, 4s and 7s are jokers.',
          'Jokers can represent any rank and suit.',
          'Multiple jokers may combine to form trails, sequences, or flushes.',
          'The server automatically picks the strongest valid hand for you.',
        ],
      },
      {
        title: 'Turn Timer',
        body: 'Each player has a limited time to act. If time expires, you are automatically packed (folded).',
      },
    ],
    examples: [
      { label: 'Single joker', original: 'A♠ 5♣ 5♥', result: 'Trail of Fives (5♠ 5♣ 5♥)' },
      {
        label: 'Pure sequence (1 joker)',
        original: '7♦ Q♦ J♦',
        result: 'Pure Sequence (K♦ Q♦ J♦)',
        note: '7♦ is wild → becomes K♦ to complete K-Q-J of diamonds.',
      },
      {
        label: 'Two jokers + fixed card',
        original: 'Q♦ 7♣ 4♠',
        result: 'Trail of Queens (Q♦ Q♣ Q♠)',
        note: 'With two jokers and one fixed rank, trail of that rank beats other combinations.',
      },
      { label: 'Double joker', original: '4♣ 7♦ 9♥', result: 'Trail of Nines (9♣ 9♦ 9♥)' },
      { label: 'Triple joker', original: 'A♠ K♣ 4♦', result: 'Trail of Aces (A♥ A♣ A♦)' },
    ],
  },
  muflis: {
    key: 'muflis',
    title: 'Muflis Rules',
    description: 'Reverse Teen Patti — the weakest classic hand wins the pot.',
    objective: 'Aim for the lowest-ranked hand. Rankings are inverted and lower cards beat higher cards.',
    handRankings: MUFLIS_HANDS,
    handRankingNote:
      'Weakest classic hand wins. High Card is strongest in Muflis; Trail is weakest. Within the same category, lower cards and lower pairs/sequences win.',
    extraSections: [
      { title: 'How to Play', body: SHARED_GAMEPLAY },
      {
        title: 'Reverse Logic',
        body: [
          'Hand rankings are reversed compared to Classic.',
          'Lower cards beat higher cards.',
          'Lower pairs beat higher pairs.',
          'Lower sequences beat higher sequences.',
        ],
      },
      {
        title: 'Turn Timer',
        body: 'Each player has a limited time to act. If time expires, you are automatically packed (folded).',
      },
    ],
    examples: [
      {
        label: 'High Card vs Trail',
        original: '2♣ 5♦ 8♥ vs A♠ A♥ A♦',
        result: '2♣ 5♦ 8♥ wins',
        note: 'High Card beats Trail in Muflis.',
      },
      {
        label: 'Lower pair wins',
        original: '5♠ 5♥ K♦ vs A♠ A♥ 7♦',
        result: '5♠ 5♥ K♦ wins',
        note: 'Pair of 5s beats pair of Aces.',
      },
      {
        label: 'Lower sequence wins',
        original: 'A♣ 2♦ 3♥ vs A♠ K♥ Q♦',
        result: 'A♣ 2♦ 3♥ wins',
        note: 'Ace-low sequence beats ace-high sequence.',
      },
    ],
  },
};

export function normalizeVariantRulesKey(code: string | null | undefined): VariantRulesKey {
  const key = (code || 'classic').trim().toLowerCase();
  if (key === 'ak47') return 'ak47';
  if (key === 'muflis') return 'muflis';
  return 'classic';
}

export function getVariantRules(code: string | null | undefined): VariantRulesContent {
  return VARIANT_RULES[normalizeVariantRulesKey(code)];
}

export function rulesPathForVariant(code: string | null | undefined): string {
  return `/rules?variant=${encodeURIComponent(formatVariantLabel(code))}`;
}
